import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IconChevronDown, IconChevronLeft, IconLogout } from "@tabler/icons-react";
import { useTheme } from "next-themes";

import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

/* The one sidebar language for every portal: Tabler icons at one weight, a
   brand rail on the active row, sentence-case section labels, and an edge
   toggle that lives in the same place open or closed. */

export const ICON = { size: 20, stroke: 1.75 } as const;
export const SUB_ICON = { size: 16, stroke: 1.75 } as const;

export const rowBase =
  "relative flex items-center rounded-md text-[13.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
export const rowIdle = "text-muted-foreground hover:bg-accent hover:text-foreground";
export const rowActive = "bg-accent text-foreground";

/** The brand rail on the sidebar's edge that marks the current section. */
export function ActiveRail() {
  return (
    <span
      aria-hidden
      className="absolute -left-2 top-2 bottom-2 w-[3px] rounded-r-full bg-brand-600"
    />
  );
}

/** A section heading when open, a hairline when collapsed. */
export function SectionHeading({ label, collapsed }: { label: string; collapsed: boolean }) {
  return collapsed ? (
    <div aria-hidden className="mx-1 mb-2 h-px bg-border" />
  ) : (
    <div className="mb-1 px-2.5 text-[11px] font-medium text-muted-foreground">{label}</div>
  );
}

/** One toggle, pinned to the edge, in the same place in both states. */
export function EdgeToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      className="absolute -right-3 top-[26px] z-10 flex size-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:border-brand-600 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <IconChevronLeft
        size={14}
        stroke={2}
        className={cn("transition-transform duration-300", collapsed && "rotate-180")}
      />
    </button>
  );
}

/** A top-level row: icon, label, optional chevron for a group. */
export function NavRow({
  id,
  to,
  label,
  icon,
  active,
  collapsed,
  expandable,
  expanded,
  onToggleExpand,
  onClick,
}: {
  id?: string;
  to: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  collapsed: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onClick?: () => void;
}) {
  return (
    <Link
      id={id}
      to={to}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={cn(
        rowBase,
        collapsed ? "mx-auto size-10 justify-center" : "h-9 w-full gap-3 px-2.5",
        active ? rowActive : rowIdle
      )}
    >
      {active && <ActiveRail />}
      <span
        className={cn(
          "flex size-5 shrink-0 items-center justify-center [&>svg]:size-5",
          active ? "text-brand-600" : "text-current"
        )}
      >
        {icon}
      </span>
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {!collapsed && expandable && (
        <button
          type="button"
          aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
          aria-expanded={expanded}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleExpand?.();
          }}
          className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
        >
          <IconChevronDown
            size={16}
            stroke={1.75}
            className={cn("transition-transform duration-200", expanded && "rotate-180")}
          />
        </button>
      )}
    </Link>
  );
}

/** A child row under a group, hanging off the tree line. */
export function SubRow({
  id,
  to,
  label,
  icon,
  active,
  onClick,
}: {
  id?: string;
  to: string;
  label: string;
  icon?: React.ReactNode;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      id={id}
      to={to}
      onClick={onClick}
      className={cn(rowBase, "h-8 w-full gap-2.5 px-2 text-[13px]", active ? rowActive : rowIdle)}
    >
      {icon && <span className={cn("shrink-0", active && "text-brand-600")}>{icon}</span>}
      <span className="flex-1 truncate">{label}</span>
    </Link>
  );
}

/** The indented container for a group's children. */
export function SubList({ children }: { children: React.ReactNode }) {
  return <div className="ml-[18px] mt-0.5 space-y-0.5 border-l border-border pl-3">{children}</div>;
}

/** A row inside the account menu. */
export function MenuRow({
  id,
  icon,
  label,
  active,
  onClick,
  trailing,
}: {
  id?: string;
  icon?: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
  trailing?: React.ReactNode;
}) {
  return (
    <button
      id={id}
      type="button"
      onClick={onClick}
      className={cn(
        rowBase,
        "h-8 w-full gap-2.5 px-2 text-[13px]",
        active ? rowActive : "text-foreground hover:bg-accent"
      )}
    >
      {icon && (
        <span className={cn("[&>svg]:size-4", active ? "text-brand-600" : "text-muted-foreground")}>
          {icon}
        </span>
      )}
      <span className="flex-1 truncate text-left">{label}</span>
      {trailing}
    </button>
  );
}

/** "Dark mode" with a switch. Follows the OS until the person picks; the
    choice then persists (next-themes). */
export function DarkModeRow() {
  const { resolvedTheme, setTheme } = useTheme();
  // next-themes resolves on the client only; hold a stable off state until then.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && resolvedTheme === "dark";
  return (
    <label className="mt-1 flex cursor-pointer items-center justify-between border-t border-border px-2 py-2 text-[13px] text-foreground">
      <span>Dark mode</span>
      <Switch
        size="sm"
        checked={isDark}
        onCheckedChange={(on) => setTheme(on ? "dark" : "light")}
        aria-label="Dark mode"
      />
    </label>
  );
}

export function SignOutRow({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(rowBase, "h-8 w-full gap-2.5 px-2 text-[13px] text-foreground hover:bg-accent")}
    >
      <IconLogout size={16} stroke={1.75} className="text-muted-foreground" />
      <span className="flex-1 text-left">Sign out</span>
    </button>
  );
}

/** Menu placeholder row while permissions or scope resolve. */
export function RowSkeleton({ collapsed }: { collapsed: boolean }) {
  return (
    <div
      className={cn(
        "flex h-9 animate-pulse items-center gap-3 rounded-md px-2.5",
        collapsed && "mx-auto w-10 justify-center px-0"
      )}
    >
      <div className="size-5 shrink-0 rounded bg-muted" />
      {!collapsed && <div className="h-3.5 w-2/3 rounded bg-muted" />}
    </div>
  );
}
